/* eslint-disable @typescript-eslint/no-explicit-any */
import * as dcmjs from 'dcmjs';
import type { DataElement } from 'dcmjs';
import { cloneDeep } from 'lodash';
import { AnonymizationStorage } from './anonymization_storage';
import { DICOMUtils } from './dicom_utils';
import { AnonymizationConfig } from './models/anonymization_config';
import { AnonymizationRule } from './models/anonymization_rule';
import {
    extendMetaAnonymizationRule,
    MetainformationAnonymizationRule,
} from './models/metainformation_anonymization_rule';
import * as config from './whitelist-config.json';

export enum ModelType {
    Segmentation,
    Dose,
    Data,
    DoseCalc,
    ImageProcessing,
    Registration,
    Translation,
}

export const getModelTypeString = (modelType: ModelType) => {
    switch (modelType) {
        case ModelType.Segmentation: {
            return 'contouring';
        }
        case ModelType.Dose: {
            return 'dose';
        }
        case ModelType.Data: {
            return 'data';
        }
        case ModelType.DoseCalc: {
            return 'dosecalc';
        }
        case ModelType.ImageProcessing: {
            return 'imageprocessing';
        }
        case ModelType.Registration: {
            return 'reg';
        }
        case ModelType.Translation: {
            return 'translation';
        }
        default: {
            throw new TypeError(`Unknown model type: ${modelType}`);
        }
    }
};

export class DICOMAnonymizerService {
    private NONUID_ANON_TAGS: string[] = ['PatientID', 'PatientName'];
    /** SOP Class UID tags contain standardized DICOM class identifiers, not patient data — never anonymize */
    private SOP_CLASS_UID_TAGS: string[] = [
        'SOPClassUID',
        'ReferencedSOPClassUID',
        'MediaStorageSOPClassUID',
    ];
    public UIDMapping: { [index: string]: string };
    /**
     * Whole data elements (`{vr, Value, _rawValue}`) for the private tags the
     * config keeps, not just their VRs — `saveToBuffer` spreads all of it
     * back into the output dict so a kept private tag round-trips as written.
     */
    public originalPrivateElements: { [index: string]: DataElement };
    private _config: AnonymizationConfig;
    private _storage: AnonymizationStorage;

    public constructor() {
        this._config = config;
        this._storage = new AnonymizationStorage();
        this.UIDMapping = {};
        this.originalPrivateElements = {};
    }

    public anonymizeFiles(
        taskUID: string,
        fileBuffers: ArrayBuffer[]
    ): ArrayBuffer[] {
        const originalDatasets = this.parseBuffers(fileBuffers, true);
        const anonymizedDatasets = this.anonymizeDatasets(
            taskUID,
            originalDatasets
        );

        if (!anonymizedDatasets) {
            return [];
        }

        return this.saveToBuffer(anonymizedDatasets, originalDatasets);
    }

    public reidentifyFiles(fileBuffers: ArrayBuffer[]) {
        const anonDatasets = this.parseBuffers(fileBuffers);
        const reidentifiedDatasets = this.reidentifyDatasets(anonDatasets);

        if (!reidentifiedDatasets) {
            return [];
        }

        return this.saveToBuffer(reidentifiedDatasets, anonDatasets);
    }

    public extractPrivateTags(modality: string): string[] {
        const conf = this.calculateEffectiveConfig(modality);

        if (conf && conf.keep_attributes) {
            return conf.keep_attributes
                .filter((tag: string) => tag.includes(','))
                .map((tag: string) => tag.replaceAll(',', ''));
        }

        return [];
    }

    public parseBuffers(
        fileBuffers: ArrayBuffer[],
        isAnonymization: boolean = false
    ): any[][] {
        return fileBuffers.map((file: ArrayBuffer) => {
            const original = dcmjs.data.DicomMessage.readFile(file);
            const keptPrivateTags = [];

            if (isAnonymization) {
                const dataset =
                    dcmjs.data.DicomMetaDictionary.naturalizeDataset(
                        original.dict
                    );
                keptPrivateTags.push(
                    ...this.extractPrivateTags(dataset.Modality)
                );
            }
            return [
                dcmjs.data.DicomMetaDictionary.naturalizeDataset(
                    this.cleanPrivateTags(original.dict, keptPrivateTags)
                ),
                dcmjs.data.DicomMetaDictionary.namifyDataset(
                    this.cleanPrivateTags(original.meta, keptPrivateTags)
                ),
            ];
        });
    }

    public cleanPrivateTags(dict: any, keptPrivateTags: string[]): any {
        const result: any = {};

        for (const tag of Object.keys(dict)) {
            const tagHex = parseInt(tag, 16);

            // Extract Group and Element from the tag
            const group = (tagHex >> 16) & 0xffff;
            const isEven = group % 2 === 0;

            // Ignore odd groups which are higher than 8
            if (!isEven && group > 8) {
                if (keptPrivateTags.includes(tag.toLowerCase())) {
                    // console.debug(`Found private tag: ${tag}, but it is configured to be kept`);
                    this.originalPrivateElements[tag] = dict[tag];
                } else {
                    // console.debug(`Ignoring private tag ${tag}`);
                    continue;
                }
            }

            result[tag] = dict[tag];
        }

        return result;
    }

    public saveToBuffer(
        datasets: any[][],
        originalDatasets: any[][]
    ): ArrayBuffer[] {
        return datasets.map((dataset: any[][], i: number) => {
            const [data, meta]: any[] = dataset;
            const ds: any = { ...data, _meta: meta, _vrMap: {} };

            const privateTags = this.extractPrivateTags(data.Modality);

            // set value representations
            if (i < originalDatasets.length) {
                const originalDataset = originalDatasets[i][0];

                const pixelData = originalDataset['_vrMap']?.PixelData;
                const rescaleIntercept =
                    originalDataset['_vrMap']?.RescaleIntercept;

                if (pixelData) {
                    ds._vrMap.PixelData = pixelData;
                }
                if (rescaleIntercept) {
                    ds._vrMap.RescaleIntercept = rescaleIntercept;
                }
            }

            const file = dcmjs.data.datasetToDict(ds);
            file.dict = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(ds);

            privateTags.forEach((tag: string) => {
                const tagLookup = tag.replaceAll(',', '').toUpperCase();
                if (tagLookup in data) {
                    const originalElement =
                        this.originalPrivateElements[tagLookup];

                    if (originalElement) {
                        // Spreads the whole original element, `_rawValue`
                        // included, which dcmjs consults on write.
                        file.dict[tagLookup] = {
                            ...originalElement,
                            Value: data[tagLookup],
                        };
                    } else {
                        file.dict[tagLookup] = {
                            vr: 'UN',
                            Value: data[tagLookup],
                        };
                    }
                }
            });

            return file.write();
        });
    }

    private processMeta(dataset: any) {
        const newDataset: { [id: string]: any } = {};

        for (const [key, value] of Object.entries(dataset)) {
            newDataset[key] = {
                vr: DICOMUtils.getValueRepresentation(key),
                Value: [value],
            };
        }

        return newDataset;
    }

    private anonymizeDatasets(taskUID: string, datasets: any[][]): any[][] {
        const anonymizedDatasets: [any, any][] = [];

        const modalities = datasets.map((dataset) =>
            dataset[0].Modality.toLowerCase()
        );
        const effectiveConfigs: {
            [id: string]: MetainformationAnonymizationRule;
        } = {};

        for (const modality of modalities) {
            effectiveConfigs[modality] =
                this.calculateEffectiveConfig(modality);
        }

        for (const [mainDataset, metainfoDataset] of datasets) {
            let originalKey: string | null = null;
            let datasetKey: string | null = null;
            const modality = mainDataset.Modality.toLowerCase();

            if (!datasetKey) {
                [originalKey, datasetKey] =
                    this._storage.initializeDatasetAnonymization(
                        taskUID,
                        mainDataset
                    );
            }

            const effectiveConfig = effectiveConfigs[modality];
            const anonymizedDataset = this.anonymizeDataset(
                mainDataset,
                effectiveConfig,
                originalKey!,
                datasetKey,
                false
            );
            const anonymizedMeta = this.anonymizeDataset(
                metainfoDataset,
                effectiveConfig.metainformation!,
                originalKey!,
                datasetKey,
                true
            );

            anonymizedDatasets.push([anonymizedDataset, anonymizedMeta]);
        }

        return anonymizedDatasets;
    }

    private reidentifyDatasets(datasets: any[][]): any[][] {
        const reidentifiedDatasets: [any, any][] = [];

        const modalities = datasets.map((dataset) =>
            dataset[0].Modality.toLowerCase()
        );
        const effectiveConfigs: {
            [id: string]: MetainformationAnonymizationRule;
        } = {};

        for (const modality of modalities) {
            effectiveConfigs[modality] =
                this.calculateEffectiveConfig(modality);
        }

        for (const [mainDataset, metainfoDataset] of datasets) {
            const patientID = mainDataset.PatientID;
            const datasetKey = patientID;

            if (!this._storage.hasDataset(datasetKey)) {
                console.error(
                    `Dataset ${datasetKey} not found in the original UID mapping!`
                );
                continue;
            }

            const modality = mainDataset.Modality.toLowerCase();
            const effectiveConfig = effectiveConfigs[modality];
            const anonymizedDataset = this.reidentifyDataset(
                mainDataset,
                effectiveConfig,
                datasetKey,
                false
            );
            const anonymizedMeta = this.reidentifyDataset(
                metainfoDataset,
                effectiveConfig.metainformation!,
                datasetKey,
                true
            );
            reidentifiedDatasets.push([anonymizedDataset, anonymizedMeta]);
        }

        return reidentifiedDatasets;
    }

    private anonymizeDataset(
        originalDataset: any,
        effectiveConfig: AnonymizationRule,
        originalKey: string,
        datasetKey: string,
        isMeta: boolean
    ): any {
        const anonymizedDataset: any = {};
        if (isMeta) {
            anonymizedDataset['FileMetaInformationVersion'] = {
                '0': 0,
                '1': 1,
            };
        } else {
            anonymizedDataset['PatientIdentityRemoved'] = 'YES';
            anonymizedDataset['DeidentificationMethod'] =
                'Per DICOM PS 3.15 AnnexE. Details in 0012,0064';
            anonymizedDataset['DeidentificationMethodCodeSequence'] = {
                CodeValue: '001',
                CodingSchemeDesignator: 'DCM',
                CodeMeaning: 'Basic Application Level Confidentiality Profile',
            };
        }

        const fixedTags = effectiveConfig.fixed_attributes
            ? Object.entries(effectiveConfig.fixed_attributes)
            : [];
        const keepTags = effectiveConfig.keep_attributes;
        const anonTags = effectiveConfig.anon_attributes;

        if (fixedTags) {
            for (const [tag, rule] of fixedTags) {
                anonymizedDataset[tag] = rule;

                if (tag in originalDataset) {
                    const originalValue = DICOMUtils.metaTagValue(
                        originalDataset[tag]
                    );
                    this._storage.saveOriginalMapping(
                        tag,
                        originalValue,
                        datasetKey
                    );
                }
            }
        }

        if (keepTags) {
            for (const tag of keepTags) {
                let tagLookup = tag;
                // Comma means a private tag
                if (tag.includes(',')) {
                    tagLookup = tag.replaceAll(',', '').toUpperCase();
                }
                if (tagLookup in originalDataset) {
                    anonymizedDataset[tagLookup] = DICOMUtils.metaTagValue(
                        originalDataset[tagLookup]
                    );
                }
            }
        }

        if (isMeta) {
            return this.processMeta(
                this.anonymizeDatasetRecursive(
                    originalDataset,
                    anonymizedDataset,
                    originalKey,
                    datasetKey,
                    anonTags || [],
                    false
                )
            );
        } else {
            return this.anonymizeDatasetRecursive(
                originalDataset,
                anonymizedDataset,
                originalKey,
                datasetKey,
                anonTags || [],
                false
            );
        }
    }

    private anonymizeDatasetRecursive(
        namedDataset: any,
        dataset: any | null,
        originalKey: string,
        datasetKey: string,
        tagsToKeep: string[],
        keepAll: boolean
    ): any {
        const originalDataset =
            dcmjs.data.DicomMetaDictionary.denaturalizeDataset(namedDataset);
        if (!dataset) {
            dataset = cloneDeep(namedDataset);
        }

        for (const tag in originalDataset) {
            // Unpunctuatable keys keep their raw form; the dictionary lookup
            // then misses and the tag is treated as Unknown, which is the
            // conservative outcome for anonymisation.
            const punctuatedTag =
                dcmjs.data.DicomMetaDictionary.punctuateTag(tag) ?? tag;
            const entry =
                dcmjs.data.DicomMetaDictionary.dictionary[punctuatedTag];
            const element = originalDataset[tag];
            const tagName = entry ? entry.name : 'Unknown';
            const vr = element.vr;
            const tagKept = tagsToKeep.includes(tagName) || keepAll;

            // Process sequence recursively
            if (vr == 'SQ') {
                if (tagKept) {
                    const updated = namedDataset[tagName]
                        .filter((sequenceItem: any) => sequenceItem)
                        .map((sequenceItem: any) =>
                            this.anonymizeDatasetRecursive(
                                sequenceItem,
                                null,
                                originalKey,
                                datasetKey,
                                [],
                                true
                            )
                        );
                    dataset[tagName] = updated;
                }
            } else if (vr == 'UI') {
                if (tagKept) {
                    if (this.SOP_CLASS_UID_TAGS.includes(tagName)) {
                        dataset[tagName] = namedDataset[tagName];
                    } else {
                        const originalValue = DICOMUtils.metaTagValue(
                            namedDataset[tagName]
                        );
                        const anonValue = this._storage.anonymizeUID(
                            originalValue,
                            originalKey,
                            datasetKey
                        );
                        dataset[tagName] = anonValue;

                        if (
                            tagName == 'SeriesInstanceUID' ||
                            tagName == 'SOPInstanceUID'
                        ) {
                            this.UIDMapping[originalValue] = anonValue;
                        }
                    }
                }
            } else if (this.NONUID_ANON_TAGS.includes(tagName)) {
                if (tagKept) {
                    const originalValue = DICOMUtils.metaTagValue(
                        namedDataset[tagName]
                    );
                    const anonValue = this._storage.anonymizeString(
                        originalValue,
                        originalKey,
                        datasetKey
                    );
                    dataset[tagName] = anonValue;
                }
            } else {
                if (keepAll) {
                    dataset[tagName] = namedDataset[tagName];
                }
            }
        }

        return dataset;
    }

    private reidentifyDataset(
        anonDataset: any,
        effectiveConfig: AnonymizationRule,
        datasetKey: string,
        isMeta: boolean
    ): any {
        const resultDataset = cloneDeep(anonDataset);
        if (isMeta) {
            resultDataset.FileMetaInformationVersion = { '0': 0, '1': 1 };
        } else {
            resultDataset.PatientIdentityRemoved = 'NO';
        }
        this.reidentifyDatasetRecursive(anonDataset, resultDataset, datasetKey);

        const fixedTags = effectiveConfig.fixed_attributes;
        const keepTags = effectiveConfig.keep_attributes;

        if (fixedTags) {
            for (const [tag] of Object.entries(fixedTags)) {
                if (
                    tag in anonDataset &&
                    this._storage.hasOriginalValue(tag, datasetKey)
                ) {
                    try {
                        const originalValue = DICOMUtils.metaTagValue(
                            this._storage.getOriginalValue(tag, datasetKey)
                        );
                        resultDataset[tag] = originalValue;
                    } catch (ex) {
                        console.error(
                            `Error during the reidentification for the fixed tags on tag ${tag}!`,
                            ex
                        );
                        throw ex;
                    }
                }
            }
        }

        if (keepTags) {
            for (const rule of keepTags) {
                try {
                    if (rule in anonDataset) {
                        resultDataset[rule] = DICOMUtils.metaTagValue(
                            anonDataset[rule]
                        );
                    }
                } catch (ex) {
                    console.error(
                        `Error during the reidentification for the keep tags on tag ${rule}!`,
                        ex
                    );
                    throw ex;
                }
            }
        }

        return resultDataset;
    }

    private reidentifyDatasetRecursive(
        anonDataset: any,
        dataset: any | null,
        datasetKey: string
    ): any {
        const rawAnon =
            dcmjs.data.DicomMetaDictionary.denaturalizeDataset(anonDataset);
        if (!dataset) {
            dataset = cloneDeep(anonDataset);
        }

        for (const tag in rawAnon) {
            // Unpunctuatable keys keep their raw form; the dictionary lookup
            // then misses and the tag is treated as Unknown, which is the
            // conservative outcome for anonymisation.
            const punctuatedTag =
                dcmjs.data.DicomMetaDictionary.punctuateTag(tag) ?? tag;
            const entry =
                dcmjs.data.DicomMetaDictionary.dictionary[punctuatedTag];
            const element = rawAnon[tag];
            const tagName = entry ? entry.name : 'Unknown';
            const vr = element.vr;

            // Process sequence recursively
            if (vr == 'SQ') {
                const updated = anonDataset[tagName]
                    .filter((sequenceItem: any) => sequenceItem)
                    .map((sequenceItem: any) =>
                        this.reidentifyDatasetRecursive(
                            sequenceItem,
                            null,
                            datasetKey
                        )
                    );
                dataset[tagName] = updated;
            } else if (vr == 'UI' || this.NONUID_ANON_TAGS.includes(tagName)) {
                const anonValue = DICOMUtils.metaTagValue(anonDataset[tagName]);

                if (this._storage.hasOriginalValue(anonValue, datasetKey)) {
                    const originalValue = DICOMUtils.metaTagValue(
                        this._storage.getOriginalValue(anonValue, datasetKey)
                    );
                    dataset[tagName] = originalValue;
                } else {
                    dataset[tagName] = DICOMUtils.metaTagValue(anonValue);
                }
            } else {
                dataset[tagName] = anonDataset[tagName];
            }
        }

        return dataset;
    }

    private calculateEffectiveConfig(
        modality: string
    ): MetainformationAnonymizationRule {
        let effectiveConfig = {
            ...this._config.generic,
        } as MetainformationAnonymizationRule;
        const configOverride =
            this._config.modality_overrides[modality.toLowerCase()];
        if (configOverride) {
            effectiveConfig = extendMetaAnonymizationRule(
                effectiveConfig,
                configOverride
            );
        }
        return effectiveConfig;
    }
}
